import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { KitchenAuthProvider, useKitchenAuth } from './context/KitchenAuthContext';
import KitchenLayout from './components/KitchenLayout';
import LoginPage          from './pages/LoginPage';
import RegisterPage       from './pages/RegisterPage';
import DashboardPage      from './pages/DashboardPage';
import OrdersPage         from './pages/OrdersPage';
import MenuPage           from './pages/MenuPage';
import SchedulePage       from './pages/SchedulePage';
import AnalyticsPage      from './pages/AnalyticsPage';
import FinancePage        from './pages/FinancePage';
import TeamPage           from './pages/TeamPage';
import RatingsPage        from './pages/RatingsPage';
import NotificationsPage  from './pages/NotificationsPage';
import SettingsPage       from './pages/SettingsPage';
import './kitchen.css';

// ── Protected route wrapper ───────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useKitchenAuth();

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F8FAFC',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🍛</div>
          <div style={{
            width: 36, height: 36,
            border: '3px solid #E2E8F0',
            borderTopColor: '#FF6B35',
            borderRadius: '50%',
            animation: 'kSpin .8s linear infinite',
            margin: '0 auto',
          }} />
          <style>{`@keyframes kSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/kitchen/login" replace />;
}

// ── Public-only route (redirect if already logged in) ─────────────────────────
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useKitchenAuth();
  if (isLoading) return null;
  return isAuthenticated ? <Navigate to="/kitchen/dashboard" replace /> : children;
}

// ── Inner router (has access to KitchenAuthContext) ───────────────────────────
function KitchenRouter() {
  return (
    <Routes>
      {/* Public auth routes */}
      <Route
        path="login"
        element={<PublicRoute><LoginPage /></PublicRoute>}
      />
      <Route
        path="register"
        element={<PublicRoute><RegisterPage /></PublicRoute>}
      />

      {/* Protected kitchen shell */}
      <Route
        path="/"
        element={<ProtectedRoute><KitchenLayout /></ProtectedRoute>}
      >
        <Route index         element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard"     element={<DashboardPage />} />
        <Route path="orders/*"      element={<OrdersPage />} />
        <Route path="menu/*"        element={<MenuPage />} />
        <Route path="schedule"      element={<SchedulePage />} />
        <Route path="analytics"     element={<AnalyticsPage />} />
        <Route path="finance"       element={<FinancePage />} />
        <Route path="team"          element={<TeamPage />} />
        <Route path="ratings"       element={<RatingsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings"      element={<SettingsPage />} />
        {/* Catch-all inside authenticated area */}
        <Route path="*"             element={<Navigate to="dashboard" replace />} />
      </Route>

      {/* Root redirect */}
      <Route path="*" element={<Navigate to="login" replace />} />
    </Routes>
  );
}

// ── Public export: wraps everything in KitchenAuthProvider ────────────────────
export default function KitchenApp() {
  return (
    <div className="k-app">
      <KitchenAuthProvider>
        <KitchenRouter />
      </KitchenAuthProvider>
    </div>
  );
}
